import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  Loader2,
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileText,
  RotateCcw,
  Send,
  User,
} from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { Card, CardHeader, CardContent } from './Card';
import { invoiceService } from '../services/invoiceService';
import { invoiceTransactionTypeService } from '../services/invoiceTransactionTypeService';
import { companyService } from '../services/companyService';
import { Company, formatCnpj } from '../types/company';
import { Invoice, InvoiceEmitError } from '../types/invoice';
import { InvoiceTransactionType } from '../types/invoiceTransactionType';
import {
  ReturnInvoiceItem,
  SelectedReturnInvoice,
  ReturnInvoiceCreatePayload,
  ReturnInvoiceDocRef,
} from '../types/invoiceReference';
import { InvoiceEmitErrorModal } from './InvoiceErrorModals';
import { normalizeInvoiceNumberSearch } from './returnInvoiceUtils';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const formatDateAAMM = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  const yy = d.getFullYear().toString().slice(2);
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${yy}${mm}`;
};

// ─── Sub-component: Invoice Search Modal ─────────────────────────

interface InvoiceSearchForReturnProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  lockedCustomerId?: number;
  lockedCustomerName?: string;
  lockedCustomerDocument?: string;
  onSelectInvoice: (invoice: Invoice) => void;
  alreadySelectedIds: number[];
}

const InvoiceSearchForReturn: React.FC<InvoiceSearchForReturnProps> = ({
  isOpen,
  onClose,
  companyId,
  lockedCustomerId,
  lockedCustomerName,
  lockedCustomerDocument,
  onSelectInvoice,
  alreadySelectedIds,
}) => {
  const [searchNumber, setSearchNumber] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [results, setResults] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // `overrides` carrega o valor recém-digitado: o debounce é agendado na mesma renderização do
  // setState, e o `searchNumber`/`searchCustomer` capturado ali ainda é o anterior (digitar "5445"
  // buscava "544"). Sem override, vale o state (lupa, Enter, paginação).
  const doSearch = useCallback(async (p = 1, overrides?: { number?: string; customer?: string }) => {
    const numberTerm = overrides?.number ?? searchNumber;
    const customerTerm = overrides?.customer ?? searchCustomer;
    setLoading(true);
    try {
      const searchParams = {
        company_id: companyId,
        customer_name: customerTerm.trim() || undefined,
        number: normalizeInvoiceNumberSearch(numberTerm),
        page: p,
        pageSize: 10,
      };
      console.debug('[ReturnInvoiceSearch] Searching invoices for return with params:', JSON.stringify(searchParams));
      const res = await invoiceService.searchInvoicesForReturn(searchParams);
      console.debug('[ReturnInvoiceSearch] API response:', {
        totalItems: res.pagination?.total,
        totalPages: res.pagination?.totalPages,
        resultsCount: res.data?.length,
        firstResults: res.data?.slice(0, 3).map(inv => ({ id: inv.invoice_id, number: inv.number, customer: inv.customer_name })),
      });
      // Filter out already selected and cancelled invoices. O número já é filtrado pela API sobre a
      // base inteira; refiltrar aqui a página já paginada só escondia notas.
      let filtered = res.data.filter(inv =>
        !alreadySelectedIds.includes(inv.invoice_id) &&
        inv.status !== 2
      );
      // If locked to a customer, filter client-side as extra safety
      if (lockedCustomerId) {
        filtered = filtered.filter(inv => inv.customer_id === lockedCustomerId);
      } else if (lockedCustomerName) {
        // Fallback: match by name + document when customer_id is not available
        filtered = filtered.filter(inv =>
          inv.customer_name === lockedCustomerName &&
          (inv.customer_document || '') === (lockedCustomerDocument || '')
        );
      }
      setResults(filtered);
      setTotalPages(res.pagination?.totalPages ?? 1);
      setPage(p);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, [companyId, searchCustomer, searchNumber, alreadySelectedIds, lockedCustomerId, lockedCustomerName, lockedCustomerDocument]);

  const scheduleSearch = (overrides: { number?: string; customer?: string }) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(1, overrides), 400);
  };

  const searchNow = () => {
    clearTimeout(debounceRef.current);
    doSearch(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchNow();
    }
  };

  // Busca inicial só ao abrir / trocar a empresa: com doSearch nas dependências, cada tecla (que
  // recria doSearch) dispararia uma busca imediata e anularia o debounce.
  useEffect(() => {
    if (isOpen && companyId) {
      doSearch(1);
    }
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Selecionar Nota Fiscal para Devolução" size="xl">
      <div className="space-y-4">
        {lockedCustomerName && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 flex items-center gap-2 text-sm text-blue-800">
            <User size={14} />
            <span>Filtrando notas do cliente: <strong>{lockedCustomerName}</strong></span>
          </div>
        )}

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              className="w-full input-field pl-9 py-2.5 text-sm"
              placeholder="Buscar por número da nota..."
              value={searchNumber}
              onChange={e => { setSearchNumber(e.target.value); scheduleSearch({ number: e.target.value }); }}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          {!lockedCustomerId && (
            <div className="relative flex-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                className="w-full input-field pl-9 py-2.5 text-sm"
                placeholder="Buscar por nome do cliente..."
                value={searchCustomer}
                onChange={e => { setSearchCustomer(e.target.value); scheduleSearch({ customer: e.target.value }); }}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
          )}
          <Button variant="outline" onClick={searchNow} disabled={loading}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          </Button>
        </div>

        <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Buscando notas fiscais...
            </div>
          ) : results.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Número</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Série</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Data</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Cliente</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Situação</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-slate-500">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {results.map(inv => (
                  <tr key={inv.invoice_id} className="hover:bg-emerald-50/50 transition-colors">
                    <td className="px-3 py-2 font-medium">{inv.number || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{inv.serie || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{formatDate(inv.generation_timestamp)}</td>
                    <td className="px-3 py-2 text-slate-700">{inv.customer_name || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(inv.total_amount)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        {inv.status === 5 ? 'Autorizada' : inv.status === 6 ? 'Emitida' : `Status ${inv.status}`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded transition-colors"
                        title="Selecionar para devolução"
                        onClick={() => onSelectInvoice(inv)}
                      >
                        <Plus size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : searched ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              Nenhuma nota fiscal encontrada com os critérios informados
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400 text-sm">
              <FileText size={32} className="mx-auto mb-2 text-slate-300" />
              Pesquise notas fiscais por número ou nome do cliente
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => doSearch(page - 1)}
              className="px-3 py-1 text-sm border rounded hover:bg-slate-50 disabled:opacity-40"
            >Anterior</button>
            <span className="text-sm text-slate-500">Página {page} de {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => doSearch(page + 1)}
              className="px-3 py-1 text-sm border rounded hover:bg-slate-50 disabled:opacity-40"
            >Próxima</button>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
        <Button variant="outline" onClick={onClose}>Fechar</Button>
      </div>
    </Dialog>
  );
};

// ─── Sub-component: Item list for a selected invoice ─────────────

interface ReturnInvoiceItemsProps {
  selected: SelectedReturnInvoice;
  onUpdateReturnType: (type: 'total' | 'partial') => void;
  onToggleItem: (itemId: number) => void;
  onChangeQuantity: (itemId: number, qty: number) => void;
  onRemove: () => void;
}

const ReturnInvoiceItems: React.FC<ReturnInvoiceItemsProps> = ({
  selected,
  onUpdateReturnType,
  onToggleItem,
  onChangeQuantity,
  onRemove,
}) => {
  const [expanded, setExpanded] = useState(true);

  const totalReturn = selected.items
    .filter(i => i.selected)
    .reduce((sum, i) => sum + i.return_quantity * i.unit_value, 0);

  return (
    <Card className="border-l-4 border-l-emerald-500">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-t-lg">
        <button
          className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <FileText size={16} className="text-emerald-600" />
          NF #{selected.invoice.number} — Série {selected.invoice.serie}
          <span className="text-slate-500 font-normal ml-2">
            {formatDate(selected.invoice.generation_timestamp)}
          </span>
          <span className="text-slate-500 font-normal">
            — {selected.invoice.customer_name}
          </span>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-emerald-700">{formatCurrency(totalReturn)}</span>
          <button
            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Remover esta nota da devolução"
            onClick={onRemove}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-3">
          {/* Return type selector */}
          <div className="flex items-center gap-4 mb-3">
            <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Tipo de Devolução:</label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`return-type-${selected.invoice.invoice_id}`}
                checked={selected.return_type === 'total'}
                onChange={() => onUpdateReturnType('total')}
                className="accent-emerald-600"
              />
              Total
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`return-type-${selected.invoice.invoice_id}`}
                checked={selected.return_type === 'partial'}
                onChange={() => onUpdateReturnType('partial')}
                className="accent-emerald-600"
              />
              Parcial
            </label>
          </div>

          {/* Items table */}
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-center w-10">
                    <span className="text-xs text-slate-500">Sel.</span>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Código</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Descrição</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-slate-500">UN</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-slate-500">Qtd Original</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-slate-500">Qtd Devolução</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Valor Unit.</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Subtotal Dev.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {selected.items.map(item => (
                  <tr
                    key={item.invoice_item_id}
                    className={`transition-colors ${item.selected ? 'bg-emerald-50/40' : 'bg-white opacity-60'}`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => onToggleItem(item.invoice_item_id!)}
                        disabled={selected.return_type === 'total'}
                        className="accent-emerald-600 w-4 h-4"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{item.code || '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{item.description || '—'}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{item.unit || 'UN'}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{item.original_quantity}</td>
                    <td className="px-3 py-2 text-center">
                      {selected.return_type === 'partial' && item.selected ? (
                        <input
                          type="number"
                          min={1}
                          max={item.original_quantity}
                          value={item.return_quantity}
                          onChange={e => onChangeQuantity(item.invoice_item_id!, Math.max(1, Math.min(item.original_quantity, Number(e.target.value) || 1)))}
                          className="w-16 text-center input-field px-1 py-1 text-sm"
                        />
                      ) : (
                        <span className={item.selected ? 'font-medium text-emerald-700' : 'text-slate-400'}>
                          {item.selected ? item.return_quantity : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(item.unit_value)}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {item.selected ? formatCurrency(item.return_quantity * item.unit_value) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ─── Main Component: ReturnInvoiceModal ──────────────────────────

interface ReturnInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  /** Pre-fill with a specific source invoice (from sales invoice list) */
  sourceInvoice?: Invoice;
}

export const ReturnInvoiceModal: React.FC<ReturnInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  sourceInvoice,
}) => {
  // ── Step state ──────────────────────────────────────────────
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [transactionTypes, setTransactionTypes] = useState<InvoiceTransactionType[]>([]);
  const [transactionTypeId, setTransactionTypeId] = useState<number | ''>('');
  const [observacoes, setObservacoes] = useState('');
  const [isSimpleTax, setIsSimpleTax] = useState(true);

  // Selected invoice for return (single invoice only — Bling limitation)
  const [selectedInvoices, setSelectedInvoices] = useState<SelectedReturnInvoice[]>([]);
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(false);

  // Customer info (locked after invoice selection)
  const [lockedCustomerId, setLockedCustomerId] = useState<number | undefined>();
  const [lockedCustomerName, setLockedCustomerName] = useState<string | undefined>();
  const [lockedCustomerDocument, setLockedCustomerDocument] = useState<string | undefined>();

  // Submission
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<InvoiceEmitError | null>(null);

  // ── Load companies ──────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      companyService.getCompanies().then(setCompanies).catch(() => {});
    }
  }, [isOpen]);

  // ── Load transaction types (only entry types) ──────────────
  useEffect(() => {
    if (companyId) {
      invoiceTransactionTypeService.getByCompanyFiltered(
        companyId as number,
        { transaction_type: ['E'] },
      ).then(types => {
        // Only active entry types
        setTransactionTypes(types.filter(t => t.active));
      }).catch(() => setTransactionTypes([]));
    } else {
      setTransactionTypes([]);
      setTransactionTypeId('');
    }
  }, [companyId]);

  // ── Reset on close ─────────────────────────────────────────
  const handleClose = () => {
    setCompanyId('');
    setTransactionTypeId('');
    setObservacoes('');
    setSelectedInvoices([]);
    setLockedCustomerId(undefined);
    setLockedCustomerName(undefined);
    setLockedCustomerDocument(undefined);
    setError(null);
    setCreateError(null);
    setCreating(false);
    setIsSimpleTax(true);
    onClose();
  };

  // ── Auto-load source invoice when modal opens ──────────────
  const sourceInvoiceLoadedRef = useRef(false);
  useEffect(() => {
    if (isOpen && sourceInvoice && !sourceInvoiceLoadedRef.current) {
      sourceInvoiceLoadedRef.current = true;
      // Auto-set company from source invoice
      if (sourceInvoice.company_id) {
        setCompanyId(sourceInvoice.company_id);
      }
      // Auto-load the source invoice
      handleInvoiceSelected(sourceInvoice);
    }
    if (!isOpen) {
      sourceInvoiceLoadedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sourceInvoice]);

  // ── Handle invoice selection (single invoice only) ─────────
  const handleInvoiceSelected = async (invoice: Invoice) => {
    // Replace any existing selection — only one invoice allowed
    // Load full invoice with items
    try {
      const fullInvoice = await invoiceService.getInvoiceById(invoice.invoice_id);
      const items: ReturnInvoiceItem[] = (fullInvoice.items || []).map((item, idx) => ({
        ...item,
        selected: true,
        return_quantity: item.quantity,
        original_quantity: item.quantity,
        source_invoice_id: invoice.invoice_id,
        source_access_key: fullInvoice.access_key,
        source_item_number: idx + 1,
      }));

      const selected: SelectedReturnInvoice = {
        invoice: fullInvoice,
        items,
        return_type: 'total',
      };

      // Replace selection — single invoice only (Bling does not support multiple referenced invoices)
      setSelectedInvoices([selected]);

      // Lock customer
      if (fullInvoice.customer_id) {
        setLockedCustomerId(fullInvoice.customer_id);
      }
      setLockedCustomerName(fullInvoice.customer_name || `NF #${fullInvoice.invoice_id}`);
      setLockedCustomerDocument(fullInvoice.customer_document || '');

      setShowInvoiceSearch(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados da nota fiscal');
    }
  };

  // ── Handle return type change ──────────────────────────────
  const handleReturnTypeChange = (invoiceId: number, type: 'total' | 'partial') => {
    setSelectedInvoices(prev => prev.map(sel => {
      if (sel.invoice.invoice_id !== invoiceId) return sel;
      return {
        ...sel,
        return_type: type,
        items: sel.items.map(item => ({
          ...item,
          selected: type === 'total' ? true : item.selected,
          return_quantity: type === 'total' ? item.original_quantity : item.return_quantity,
        })),
      };
    }));
  };

  // ── Toggle item selection ──────────────────────────────────
  const handleToggleItem = (invoiceId: number, itemId: number) => {
    setSelectedInvoices(prev => prev.map(sel => {
      if (sel.invoice.invoice_id !== invoiceId) return sel;
      return {
        ...sel,
        items: sel.items.map(item =>
          item.invoice_item_id === itemId
            ? { ...item, selected: !item.selected }
            : item
        ),
      };
    }));
  };

  // ── Change return quantity ─────────────────────────────────
  const handleChangeQuantity = (invoiceId: number, itemId: number, qty: number) => {
    setSelectedInvoices(prev => prev.map(sel => {
      if (sel.invoice.invoice_id !== invoiceId) return sel;
      return {
        ...sel,
        items: sel.items.map(item =>
          item.invoice_item_id === itemId
            ? { ...item, return_quantity: qty }
            : item
        ),
      };
    }));
  };

  // ── Remove invoice from selection ──────────────────────────
  const handleRemoveInvoice = (_invoiceId: number) => {
    // Single invoice mode — always clear everything
    setSelectedInvoices([]);
    setLockedCustomerId(undefined);
    setLockedCustomerName(undefined);
    setLockedCustomerDocument(undefined);
  };

  // ── Computed values ────────────────────────────────────────
  const allSelectedItems = selectedInvoices.flatMap(sel =>
    sel.items.filter(i => i.selected)
  );
  const totalReturnValue = allSelectedItems.reduce(
    (sum, item) => sum + item.return_quantity * item.unit_value, 0
  );

  // ── Create return invoice ──────────────────────────────────
  const handleCreate = async () => {
    if (!companyId) { setError('Selecione a empresa.'); return; }
    if (!transactionTypeId) { setError('Selecione a natureza da operação.'); return; }
    if (selectedInvoices.length === 0) { setError('Selecione uma nota fiscal para devolução.'); return; }
    if (allSelectedItems.length === 0) { setError('Selecione pelo menos um item para devolução.'); return; }

    // Get customer info from first invoice
    const firstInvoice = selectedInvoices[0].invoice;
    if (!lockedCustomerId) {
      setError('As notas selecionadas não possuem cliente vinculado (customer_id). Para gerar uma devolução, as notas de origem precisam ter um cliente associado no sistema.');
      return;
    }

    // Validate all selected invoices have access_key
    const missingAccessKey = selectedInvoices.filter(sel => !sel.invoice.access_key);
    if (missingAccessKey.length > 0) {
      const nums = missingAccessKey.map(sel => `#${sel.invoice.number || sel.invoice.invoice_id}`).join(', ');
      setError(`As seguintes notas não possuem chave de acesso (obrigatória para devolução): ${nums}. Somente notas autorizadas/emitidas com chave de acesso podem ser referenciadas.`);
      return;
    }

    // Build documentos_referenciados
    const docRefs: ReturnInvoiceDocRef[] = selectedInvoices.map(sel => ({
      modelo: '55',
      data: formatDateAAMM(sel.invoice.generation_timestamp),
      numero: sel.invoice.number?.toString() || '',
      serie: sel.invoice.serie?.toString() || '',
      chave_acesso: sel.invoice.access_key!,
    }));

    // Build items with references
    const items = allSelectedItems.map(item => ({
      code: item.code,
      description: item.description,
      unit: item.unit || 'UN',
      quantity: item.return_quantity,
      unit_value: item.unit_value,
      ncm: item.ncm,
      cfop: item.cfop,
      type: item.type || 'P',
      origin: item.origin ?? 0,
      gtin: item.gtin,
      cest: item.cest,
      documento_referenciado: {
        chave_acesso: item.source_access_key || '',
        numero_item: (item.source_item_number || 1).toString(),
      },
    }));

    const payload: ReturnInvoiceCreatePayload = {
      company_id: companyId as number,
      invoice_transaction_type_id: transactionTypeId as number,
      customer_id: lockedCustomerId!,
      customer_name: firstInvoice.customer_name || '',
      customer_document: firstInvoice.customer_document || '',
      customer_tipo_pessoa: (firstInvoice.customer_document?.replace(/\D/g, '') || '').length === 14 ? 'J' : 'F',
      // 0 = sentinela "derivar no backend, sem checagem de divergência": a emissão resolve
      // o contribuinte/IE via consulta cadastro SEFAZ + cadastro do cliente
      // (services.ReconcileDestFiscal), pois a nota tem customer_id.
      customer_contribuinte: 0,
      customer_phone: firstInvoice.customer_phone,
      customer_email: firstInvoice.ship_cust_email,
      ship_addr_street: firstInvoice.ship_addr_street,
      ship_addr_num: firstInvoice.ship_addr_num,
      ship_addr_compl: firstInvoice.ship_addr_compl,
      ship_addr_district: firstInvoice.ship_addr_district,
      ship_addr_city: firstInvoice.ship_addr_city,
      ship_addr_state: firstInvoice.ship_addr_state,
      ship_addr_zip: firstInvoice.ship_addr_zip,
      observacoes: observacoes || undefined,
      is_simple_tax: isSimpleTax,
      referenced_invoice_ids: selectedInvoices.map(sel => sel.invoice.invoice_id),
      documentos_referenciados: docRefs,
      items,
    };

    try {
      setCreating(true);
      setError(null);
      setCreateError(null);

      const result = await invoiceService.createReturnInvoice(payload);

      if (result.success) {
        handleClose();
        onSuccess(result.data?.pending
          ? (result.data.message || 'NF-e em processamento na SEFAZ; será concluída automaticamente.')
          : 'Nota fiscal de devolução criada com sucesso. Aguarde alguns instantes para que o status seja atualizado.');
      } else if (result.error && typeof result.error === 'object') {
        // Structured error from backend (VALIDATION_ERROR, BLING_API_ERROR, etc.)
        setCreateError(result.error as InvoiceEmitError);
      } else if (result.details && typeof result.details === 'object') {
        // HTTP 400 — field-level validation errors
        const fieldMessages = Object.entries(result.details)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join('; ');
        setCreateError({
          type: 'VALIDATION_ERROR',
          message: result.message || 'Erro de validação',
          description: fieldMessages,
          fields: [],
        });
      } else {
        // Fallback: any other error format
        const msg = result.message || (typeof result.error === 'string' ? result.error : 'Resposta inesperada do servidor.');
        setCreateError({
          type: 'INTERNAL_ERROR',
          message: 'Erro ao criar nota de devolução',
          description: msg,
          fields: [],
        });
      }
    } catch (err) {
      setCreateError({
        type: 'INTERNAL_ERROR',
        message: 'Erro ao criar nota fiscal de devolução',
        description: err instanceof Error ? err.message : 'Erro inesperado na comunicação com o servidor.',
        fields: [],
      });
    } finally {
      setCreating(false);
    }
  };

  const canCreate = Boolean(
    companyId &&
    transactionTypeId &&
    selectedInvoices.length > 0 &&
    allSelectedItems.length > 0 &&
    !creating
  );

  if (!isOpen && !createError) return null;

  return (
    <>

      <Dialog isOpen={isOpen} onClose={handleClose} title="Nova Nota Fiscal de Devolução" size="xl">
        <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
            </div>
          )}

          {/* Step 1: Company & Transaction Type */}
          <Card>
            <CardHeader title="1. Dados Gerais" />
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Empresa *</label>
                  <select
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={companyId}
                    onChange={e => {
                      const val = e.target.value ? Number(e.target.value) : '';
                      setCompanyId(val);
                      // Reset selections when company changes
                      setSelectedInvoices([]);
                      setLockedCustomerId(undefined);
                      setLockedCustomerName(undefined);
                    }}
                    disabled={selectedInvoices.length > 0}
                  >
                    <option value="">Selecione...</option>
                    {companies.map(c => (
                      <option key={c.company_id} value={c.company_id}>
                        {c.name} — {formatCnpj(c.document)}
                      </option>
                    ))}
                  </select>
                  {selectedInvoices.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">Remova as notas para alterar a empresa</p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Natureza da Operação *</label>
                  <select
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    value={transactionTypeId}
                    onChange={e => setTransactionTypeId(e.target.value ? Number(e.target.value) : '')}
                    disabled={!companyId}
                  >
                    <option value="">Selecione...</option>
                    {transactionTypes.map(t => (
                      <option key={t.invoice_transaction_type_id} value={t.invoice_transaction_type_id}>
                        {t.invoice_transaction_type_name}
                      </option>
                    ))}
                  </select>
                  {!companyId && <p className="text-xs text-slate-400 mt-1">Selecione a empresa primeiro</p>}
                  {companyId && transactionTypes.length === 0 && (
                    <p className="text-xs text-orange-500 mt-1">Nenhuma natureza de operação de entrada encontrada</p>
                  )}
                </div>

                <div className="flex items-end gap-2 pb-1">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSimpleTax}
                      onChange={e => setIsSimpleTax(e.target.checked)}
                      className="accent-emerald-600 w-4 h-4"
                    />
                    Simples Nacional
                  </label>
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Observações</label>
                  <textarea
                    className="mt-1 w-full input-field px-3 py-2 text-sm"
                    rows={2}
                    value={observacoes}
                    onChange={e => setObservacoes(e.target.value)}
                    placeholder="Observações da nota de devolução..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step 2: Select invoice */}
          <Card>
            <CardHeader title="2. Nota Fiscal de Origem" />
            <CardContent>
              {lockedCustomerName && (
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-2.5 flex items-center gap-2 text-sm text-blue-800">
                  <User size={14} />
                  <span>Cliente: <strong>{lockedCustomerName}</strong></span>
                </div>
              )}

              {selectedInvoices.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <RotateCcw size={32} className="mx-auto mb-2" />
                  <p className="text-sm">Nenhuma nota fiscal selecionada para devolução</p>
                  <p className="text-xs mt-1">Selecione uma nota fiscal de venda para gerar a devolução</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedInvoices.map(sel => (
                    <ReturnInvoiceItems
                      key={sel.invoice.invoice_id}
                      selected={sel}
                      onUpdateReturnType={type => handleReturnTypeChange(sel.invoice.invoice_id, type)}
                      onToggleItem={itemId => handleToggleItem(sel.invoice.invoice_id, itemId)}
                      onChangeQuantity={(itemId, qty) => handleChangeQuantity(sel.invoice.invoice_id, itemId, qty)}
                      onRemove={() => handleRemoveInvoice(sel.invoice.invoice_id)}
                    />
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <Button
                  variant="outline"
                  icon={selectedInvoices.length > 0 ? <RotateCcw size={16} /> : <Plus size={16} />}
                  onClick={() => setShowInvoiceSearch(true)}
                  disabled={!companyId}
                >
                  {selectedInvoices.length > 0 ? 'Trocar Nota Fiscal' : 'Selecionar Nota Fiscal'}
                </Button>

                {allSelectedItems.length > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{allSelectedItems.length} item(ns) para devolução</p>
                    <p className="text-lg font-bold text-emerald-700">{formatCurrency(totalReturnValue)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 mt-4">
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button
            icon={creating ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            disabled={!canCreate}
            onClick={handleCreate}
          >
            {creating ? 'Criando...' : 'Criar Nota de Devolução'}
          </Button>
        </div>
      </Dialog>

      {/* Invoice search sub-modal */}
      {showInvoiceSearch && companyId && (
        <InvoiceSearchForReturn
          isOpen={showInvoiceSearch}
          onClose={() => setShowInvoiceSearch(false)}
          companyId={companyId as number}
          lockedCustomerId={lockedCustomerId}
          lockedCustomerName={lockedCustomerName}
          lockedCustomerDocument={lockedCustomerDocument}
          onSelectInvoice={handleInvoiceSelected}
          alreadySelectedIds={selectedInvoices.map(s => s.invoice.invoice_id)}
        />
      )}

      {/* Error modal — rendered AFTER Dialog so it appears on top (z-50 stacking) */}
      {createError && (
        <InvoiceEmitErrorModal
          error={createError}
          contextLabel="na Devolução"
          onClose={() => setCreateError(null)}
        />
      )}
    </>
  );
};
